/* eslint-disable max-lines-per-function */
import { Icon16DownloadOutline, Icon20DownloadOutline } from '@vkontakte/icons';
import clsx from 'clsx';
import React, { type FormEvent, type ReactElement, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SharingNewDropdown } from 'reactApp/components/SharingNewBiz/Dropdown/SharingNewDropdown';
import {
    DOWNLOAD_FILE_LIMIT_ACTIVE_OPTIONS,
    DOWNLOAD_FILE_OPTIONS,
    QA_VALUE,
    TOOLTIP_OFFSET,
} from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { DownloadAccess } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import styles from 'reactApp/components/SharingNewBiz/Weblink/SharingNewWeblink.css';
import { SharingDropdown } from 'reactApp/components/SharingWindow/Dropdown/SharingDropdown';
import b2cStyles from 'reactApp/components/SharingWindow/Weblink/SharingWeblink.css';
import { useOutsideClick } from 'reactApp/hooks/useOutsideClick';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { updateWeblinkCountDownloadsRequest } from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import type { TextInputModalProps } from 'reactApp/ui/TextInputModal/TextInputModal.types';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';

const DEFAULT_LIMITED_DOWNLOADS_COUNT = 10;

interface Props {
    item?: PublishItem;
    isDownloadable?: boolean;
    redesign?: boolean;
}

interface DownloadsLimitedDropdownProps {
    redesign?: boolean;
    downloadOption: (typeof DOWNLOAD_FILE_OPTIONS)[keyof typeof DOWNLOAD_FILE_OPTIONS];
    downloadCountLeft: number;
    downloadCountTotal: number;
    renderDownloadCountDropdown: (onClick: void) => ReactElement;
    onClickItem: (id: DownloadAccess) => void;
    isPhone: boolean;
    isInputActive: boolean;
    onDownloadCountSubmit: (e: FormEvent<HTMLFormElement>) => void;
    inputRef: React.RefObject<HTMLInputElement>;
    onDownloadCountTotalChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: () => void;
    handleCountWrapperClick: () => void;
}

const DownloadsLimitedDropdown = ({
    redesign,
    downloadOption,
    downloadCountLeft,
    downloadCountTotal,
    renderDownloadCountDropdown,
    onClickItem,
    isPhone,
    isInputActive,
    onDownloadCountSubmit,
    inputRef,
    onDownloadCountTotalChange,
    handleBlur,
    handleCountWrapperClick,
}: DownloadsLimitedDropdownProps) => {
    if (redesign) {
        return (
            <div className={clsx(styles.dropdown, styles.downloadDropdown)}>
                <Dropdown
                    withinDialog
                    theme="octavius"
                    qaValue={downloadOption?.id}
                    as={redesign ? SharingDropdown : SharingNewDropdown}
                    value={`${downloadCountTotal - downloadCountLeft} из ${downloadCountTotal}`}
                    content={renderDownloadCountDropdown}
                    onClickItem={onClickItem}
                    placement={tooltipPlacements.BOTTOM_LEFT}
                    qaValueContent={QA_VALUE.downloadDropdownContent}
                    tooltipOffset={TOOLTIP_OFFSET}
                    animatedTooltip
                    description="Количество скачиваний"
                    tooltipClassName={isPhone ? styles.tooltip : undefined}
                    isPhone={isPhone}
                    noPageOffset={isPhone}
                    redesign={redesign}
                />
            </div>
        );
    } else if (isInputActive) {
        return (
            <form onSubmit={onDownloadCountSubmit}>
                <input
                    ref={inputRef}
                    className={styles.inputCount}
                    type="number"
                    defaultValue={downloadCountTotal}
                    onChange={onDownloadCountTotalChange}
                    onBlur={handleBlur}
                    autoFocus
                />
            </form>
        );
    }

    return (
        <div className={styles.countWrapper} onClick={handleCountWrapperClick}>
            {downloadCountLeft} из {downloadCountTotal}
        </div>
    );
};

export const DownloadsCountDropdown: React.FC<Props> = memo(({ item, isDownloadable, redesign }) => {
    const countRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isInputActive, setIsInputActive] = useState(false);
    const [downloadCountLeft, setDownloadCountLeft] = useState<undefined | number>(undefined);
    const [downloadCountTotal, setDownloadCountTotal] = useState<undefined | number>(undefined);
    const dispatch = useDispatch();
    const isPhone = EnvironmentSelectors.isPhone();
    const checked = downloadCountLeft !== undefined && downloadCountTotal !== undefined ? DownloadAccess.Limited : DownloadAccess.Unlimited;

    const updateCountDownloads = useCallback(
        (count_downloads: number) => {
            if (item && !isNaN(count_downloads)) {
                dispatch(updateWeblinkCountDownloadsRequest({ item, count_downloads }));
            }
        },
        [dispatch, item]
    );

    const handleBlur = useCallback(() => {
        if (inputRef.current?.value === '') {
            updateCountDownloads(0);
            setDownloadCountLeft(0);
        }
        setIsInputActive(false);
    }, [updateCountDownloads]);

    const handleCountWrapperClick = () => setIsInputActive(true);

    const onClickItem = useCallback(
        (id: DownloadAccess) => {
            if (id === DownloadAccess.Limited) {
                if (redesign) {
                    openPopupHelper<TextInputModalProps>({
                        popupName: popupNames.TEXT_INPUT_MODAL,
                        data: {
                            id: 'download-limit-modal',
                            title: 'Установите ограничение на скачивание',
                            buttonAcceptText: 'Установить',
                            buttonRejectText: 'Отменить',
                            placeholder: 'Максимальное количество скачиваний',
                            validator: (value) => {
                                const regex = /^\d*$/;

                                if (regex.test(value)) {
                                    return true;
                                }

                                return 'Можно использовать только цифры';
                            },
                            onActionClick: (value) => {
                                updateCountDownloads(value ? Number.parseInt(value) : DEFAULT_LIMITED_DOWNLOADS_COUNT);
                            },
                        },
                    });
                    return;
                }

                return updateCountDownloads(DEFAULT_LIMITED_DOWNLOADS_COUNT);
            }

            updateCountDownloads(0);
        },
        [redesign, updateCountDownloads]
    );

    const onDownloadCountTotalChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateCountDownloads(parseInt(e.target.value));
        },
        [updateCountDownloads]
    );

    const onDownloadCountSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        inputRef.current?.blur();
    }, []);

    const renderDownloadCountDropdown = useCallback(
        (onClick: void): ReactElement => {
            const list = Object.values(checked === DownloadAccess.Limited ? DOWNLOAD_FILE_LIMIT_ACTIVE_OPTIONS : DOWNLOAD_FILE_OPTIONS).map(
                (item) => ({
                    id: item.id,
                    text: item.dropdownText,
                    check: item.id === checked && checked !== DownloadAccess.Limited,
                    qaValue: item.id,
                })
            );

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
        },
        [checked]
    );

    useOutsideClick(countRef, handleBlur);

    useEffect(() => {
        setDownloadCountLeft(item?.count_downloads_left);
    }, [item?.count_downloads_left]);

    useEffect(() => {
        setDownloadCountTotal(item?.count_downloads_total);
    }, [item?.count_downloads_total]);

    const downloadOption = DOWNLOAD_FILE_OPTIONS[checked];

    return (
        <div className={clsx(styles.rightsItem, { [styles.rightsItemNoMargin]: true })} data-qa-item={QA_VALUE.downloadItem}>
            {redesign ? (
                <span className={clsx(b2cStyles.rightTitle, b2cStyles.tabs)}>
                    <Icon20DownloadOutline />
                    Количество скачиваний
                </span>
            ) : (
                <div className={styles.titleWrapper}>
                    <span className={styles.icon}>
                        <Icon16DownloadOutline />
                    </span>
                    <span className={styles.rightsTitle}>Количество скачиваний</span>
                </div>
            )}

            {isDownloadable && (!redesign || (redesign && checked !== DownloadAccess.Limited)) && (
                <div className={clsx(styles.dropdown, styles.downloadDropdown)}>
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={downloadOption?.id}
                        as={redesign ? SharingDropdown : SharingNewDropdown}
                        value={downloadOption.text}
                        content={renderDownloadCountDropdown}
                        onClickItem={onClickItem}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.downloadDropdownContent}
                        tooltipOffset={TOOLTIP_OFFSET}
                        animatedTooltip
                        description="Количество скачиваний"
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                        noPageOffset={isPhone}
                        redesign={redesign}
                    />
                </div>
            )}

            {!isDownloadable && (
                <span className={clsx(styles.notDownloadable, styles.redesign)}>{redesign ? 'без ограничений' : 'Без ограничений'}</span>
            )}

            {isDownloadable && checked === DownloadAccess.Limited && (
                <div ref={countRef} className={styles.downloadInputWrapper}>
                    <DownloadsLimitedDropdown
                        redesign={redesign}
                        downloadOption={downloadOption}
                        downloadCountLeft={downloadCountLeft ?? 0}
                        downloadCountTotal={downloadCountTotal ?? 0}
                        renderDownloadCountDropdown={renderDownloadCountDropdown}
                        onClickItem={onClickItem}
                        isPhone={isPhone}
                        isInputActive={isInputActive}
                        onDownloadCountSubmit={onDownloadCountSubmit}
                        inputRef={inputRef}
                        onDownloadCountTotalChange={onDownloadCountTotalChange}
                        handleBlur={handleBlur}
                        handleCountWrapperClick={handleCountWrapperClick}
                    />
                </div>
            )}
        </div>
    );
});

DownloadsCountDropdown.displayName = 'DownloadsCountDropdown';
